import React from 'react';
import { Link } from 'react-router-dom';
import { Home, Search, ArrowLeft, School, Globe, AlertCircle } from 'lucide-react';

function NotFound() {
  const quickLinks = [
    { name: 'Home', path: '/', icon: Home },
    { name: 'About Us', path: '/about', icon: School },
    { name: 'Academics', path: '/academics', icon: Globe },
    { name: 'Contact', path: '/contact', icon: Search },
  ];

  const popularPages = [
    { name: 'Admission Process', path: '/admissions' },
    { name: 'Fee Structure', path: '/fees' },
    { name: 'School Gallery', path: '/gallery' },
    { name: 'Results', path: '/results' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50 flex items-center justify-center px-4">
      <div className="max-w-4xl mx-auto text-center">
        {/* Animated 404 */}
        <div className="relative mb-8">
          <div className="text-[180px] md:text-[250px] font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-blue-600 to-cyan-500 leading-none opacity-10 select-none">
            404
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative">
              <div className="w-48 h-48 md:w-64 md:h-64 bg-gradient-to-r from-blue-100 to-cyan-100 rounded-full flex items-center justify-center shadow-2xl">
                <div className="w-32 h-32 md:w-40 md:h-40 bg-gradient-to-r from-blue-200 to-cyan-200 rounded-full flex items-center justify-center shadow-inner">
                  <AlertCircle className="w-20 h-20 md:w-24 md:h-24 text-blue-600" />
                </div>
              </div>
              <div className="absolute -top-2 -right-2 md:-top-4 md:-right-4 animate-pulse">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-r from-red-400 to-pink-500 rounded-full flex items-center justify-center shadow-lg">
                  <span className="text-white font-bold text-2xl md:text-3xl">!</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Message */}
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
          Oops! Page Not Found
        </h1>
        <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
          The page you're looking for seems to have wandered off. Don't worry, 
          let's get you back on track!
        </p>

        {/* Search Bar */}
        <div className="max-w-md mx-auto mb-12">
          <div className="relative">
            <input
              type="text"
              placeholder="What are you looking for?"
              className="w-full px-6 py-4 pl-14 rounded-2xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all shadow-lg"
            />
            <Search className="absolute left-5 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <button className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-6 py-2 rounded-xl hover:shadow-lg transition-shadow">
              Search
            </button>
          </div>
        </div>

        {/* Quick Links */}
        <div className="mb-12">
          <h3 className="text-2xl font-semibold text-gray-800 mb-6">
            Quick Navigation
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {quickLinks.map((link, index) => {
              const Icon = link.icon;
              return (
                <Link
                  key={index}
                  to={link.path}
                  className="group bg-white rounded-xl p-4 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100"
                >
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-50 to-cyan-50 flex items-center justify-center mb-3 group-hover:from-blue-100 group-hover:to-cyan-100 transition-all">
                      <Icon className="w-6 h-6 text-blue-600" />
                    </div>
                    <span className="font-medium text-gray-900">{link.name}</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Popular Pages */}
        <div className="mb-12">
          <h3 className="text-2xl font-semibold text-gray-800 mb-6">
            Popular Pages
          </h3>
          <div className="flex flex-wrap justify-center gap-3">
            {popularPages.map((page, index) => (
              <Link
                key={index}
                to={page.path}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-gray-50 to-gray-100 rounded-full hover:from-blue-50 hover:to-cyan-50 text-gray-700 hover:text-blue-700 transition-all duration-300 shadow-sm hover:shadow-md border border-gray-200 hover:border-blue-200"
              >
                <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                <span>{page.name}</span>
              </Link>
            ))}
          </div>
        </div>

        {/* Main CTA */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <Link
            to="/"
            className="group inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-semibold rounded-xl hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            Back to Homepage
          </Link>
          <button
            onClick={() => window.history.back()}
            className="px-8 py-4 bg-white border-2 border-blue-600 text-blue-600 font-semibold rounded-xl hover:bg-blue-50 transition-all duration-300"
          >
            Go Back
          </button>
        </div>

        {/* Decorative Elements */}
        <div className="mt-16 relative">
          <div className="absolute -top-6 left-1/4 animate-bounce">
            <div className="w-4 h-4 rounded-full bg-blue-400"></div>
          </div>
          <div className="absolute -top-3 right-1/3 animate-bounce" style={{ animationDelay: '0.2s' }}>
            <div className="w-3 h-3 rounded-full bg-cyan-400"></div>
          </div>
          <div className="absolute -top-8 right-1/4 animate-bounce" style={{ animationDelay: '0.4s' }}>
            <div className="w-5 h-5 rounded-full bg-blue-300"></div>
          </div>
          
          <p className="text-gray-500 text-sm">
            If you believe this is an error, please{' '}
            <a href="mailto:support@gnyanodaya.edu" className="text-blue-600 hover:underline">
              contact our support team
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default NotFound;